import { useState, useEffect } from 'react'
import { createClient, getUserApiKey } from '@/lib/supabase-client'
import Sidebar from './Sidebar'
import DashboardHeader from './DashboardHeader'

type TabType = 'basic' | 'stream' | 'batch' | 'with-music' | 'stt' | 'change-voice' | 'add-music'

export default function TTSPage() {
  const [activeTab, setActiveTab] = useState<TabType>('basic')
  const [text, setText] = useState('')
  const [language, setLanguage] = useState('en')
  const [speed, setSpeed] = useState(1.0)
  const [voiceName, setVoiceName] = useState('')
  const [pitch, setPitch] = useState(0.0)
  const [volumeGainDb, setVolumeGainDb] = useState(0.0)
  const [chunkSize, setChunkSize] = useState(200)
  const [pauseDuration, setPauseDuration] = useState(800)
  const [musicVolume, setMusicVolume] = useState(-20)
  const [fadeDuration, setFadeDuration] = useState(2000)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [availableVoices, setAvailableVoices] = useState<any[]>([])
  const [loadingVoices, setLoadingVoices] = useState(false)
  const [sttResult, setSttResult] = useState<string | null>(null)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [musicFile, setMusicFile] = useState<File | null>(null)
  const [batchProgress, setBatchProgress] = useState<{
    status: 'processing' | 'completed' | 'error'
    startTime: number
    textLength: number
    audioUrl?: string
    error?: string
    progressPercentage?: number
  } | null>(null)
  const [progressInterval, setProgressInterval] = useState<ReturnType<typeof setInterval> | null>(null)
  const supabase = createClient()
  // TTS API endpoints are ONLY on Cloud Run: api.gistmag.co.uk/tts (not on Supabase)
  const apiBaseUrl = 'https://api.gistmag.co.uk'

  // Load saved batch progress on mount
  useEffect(() => {
    const savedProgress = localStorage.getItem('gistmag_batch_tts_progress')
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress)
        // Check if progress is recent (within last 24 hours)
        const hoursSinceStart = (Date.now() - progress.startTime) / (1000 * 60 * 60)
        if (hoursSinceStart < 24) {
          setBatchProgress(progress)
          if (progress.status === 'completed' && progress.audioUrl) {
            // Restore audio URL (could be blob URL or data URL)
            setAudioUrl(progress.audioUrl)
          }
          if (progress.status === 'error') {
            setError(progress.error || 'Batch processing failed')
          }
          // If still processing, show that it's in progress (though the actual request may have completed)
          // The user can check the result or restart if needed
        } else {
          // Clear old progress
          localStorage.removeItem('gistmag_batch_tts_progress')
        }
      } catch (e) {
        console.error('Error loading saved progress:', e)
        localStorage.removeItem('gistmag_batch_tts_progress')
      }
    }
  }, [])

  // Clean up progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }, [progressInterval])

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    if (batchProgress) {
      localStorage.setItem('gistmag_batch_tts_progress', JSON.stringify(batchProgress))
    } else {
      localStorage.removeItem('gistmag_batch_tts_progress')
    }
  }, [batchProgress])

  // Auto-load voices when language changes
  useEffect(() => {
    // Reset voices when language changes to prevent stale data
    setAvailableVoices([])
    loadVoices()
  }, [language])
  
  // Load voices on mount if not already loaded
  useEffect(() => {
    if (availableVoices.length === 0 && !loadingVoices) {
      loadVoices()
    }
  }, [])

  const loadVoices = async () => {
    // Prevent multiple simultaneous requests
    if (loadingVoices) {
      return
    }
    
    setLoadingVoices(true)
    try {
      // Add timeout to prevent hanging
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout
      
      const response = await fetch(`${apiBaseUrl}/tts/voices?language=${language}`, {
        method: 'GET',
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      
      if (response.ok) {
        const data = await response.json()
        setAvailableVoices(data.voices || [])
        console.log('Loaded voices:', data.voices)
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to load voices' }))
        console.error('Error loading voices:', errorData)
        // Set empty array on error so UI doesn't show stale data
        setAvailableVoices([])
      }
    } catch (err: any) {
      console.error('Error loading voices:', err)
      // Set empty array on error
      setAvailableVoices([])
      if (err.name === 'AbortError') {
        console.error('Voice loading timed out')
      }
    } finally {
      setLoadingVoices(false)
    }
  }

  const getApiKey = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) {
      throw new Error('Please sign in to use TTS')
    }

    const apiKey = await getUserApiKey(supabase, session.user.id)

    if (!apiKey) {
      throw new Error('Unable to retrieve API key. Please try refreshing the page.')
    }

    return apiKey
  }

  const downloadAudio = (blob: Blob, filename: string = 'batch-speech.mp3') => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const generateTTS = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const apiKey = await getApiKey()

      const response = await fetch(`${apiBaseUrl}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          text: text,
          language: language,
          speed: speed,
          voice_name: voiceName || undefined,
          pitch: pitch,
          volume_gain_db: volumeGainDb,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to generate speech')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while generating speech')
    } finally {
      setLoading(false)
    }
  }

  const generateStream = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const apiKey = await getApiKey()

      const response = await fetch(`${apiBaseUrl}/tts/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          text: text,
          language: language,
          chunk_size: chunkSize,
          speed: speed,
          voice_name: voiceName || undefined,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to stream speech')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while streaming speech')
    } finally {
      setLoading(false)
    }
  }

  const generateBatch = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    // Clear any existing progress interval
    if (progressInterval) {
      clearInterval(progressInterval)
    }

    const startTime = Date.now()
    // Estimate processing time: ~1 second per 1000 characters (conservative estimate)
    const estimatedDuration = Math.max(5000, (text.length / 1000) * 1000) // Minimum 5 seconds

    // Save initial progress state
    const progressState = {
      status: 'processing' as const,
      startTime: startTime,
      textLength: text.length,
      progressPercentage: 0,
    }
    setBatchProgress(progressState)

    // Start progress interval to update percentage
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      // Calculate progress: start at 5%, cap at 95% until actually done
      // This gives a smooth progress indication without reaching 100% prematurely
      const progress = Math.min(95, 5 + (elapsed / estimatedDuration) * 90)
      setBatchProgress((prev) => {
        if (prev && prev.status === 'processing') {
          return {
            ...prev,
            progressPercentage: Math.round(progress),
          }
        }
        return prev
      })
    }, 200) // Update every 200ms

    setProgressInterval(interval)

    try {
      const apiKey = await getApiKey()

      const response = await fetch(`${apiBaseUrl}/tts/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          api_key: apiKey,
          text: text,
          language: language,
          pause_duration: pauseDuration,
          speed: speed,
          voice_name: voiceName || undefined,
        }),
      })

      // Clear progress interval
      if (interval) {
        clearInterval(interval)
        setProgressInterval(null)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        let errorMessage = errorData.error || errorData.message || 'Failed to generate batch speech'
        
        setError(errorMessage)
        
        // Save error state
        setBatchProgress({
          ...progressState,
          status: 'error',
          error: errorMessage,
          progressPercentage: 0,
        })
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()

      // Create a blob URL for in-page playback
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)

      // Auto-download the audio file for the user
      downloadAudio(audioBlob, 'batch-speech.mp3')

      // Mark batch as completed (we no longer persist the full audio in localStorage
      // to avoid huge base64 strings that can crash the tab)
      setBatchProgress({
        ...progressState,
        status: 'completed',
        audioUrl: url,
        progressPercentage: 100,
      })
    } catch (err: any) {
      // Clear progress interval on error
      if (interval) {
        clearInterval(interval)
        setProgressInterval(null)
      }

      const errorMessage = err.message || 'An error occurred while generating batch speech'
      setError(errorMessage)
      
      // Save error state
      setBatchProgress({
        status: 'error',
        startTime: Date.now(),
        textLength: text.length,
        error: errorMessage,
        progressPercentage: 0,
      })
    } finally {
      setLoading(false)
    }
  }

  const clearBatchProgress = () => {
    if (progressInterval) {
      clearInterval(progressInterval)
      setProgressInterval(null)
    }
    setBatchProgress(null)
    setAudioUrl(null)
    setError(null)
    localStorage.removeItem('gistmag_batch_tts_progress')
  }

  const generateWithMusic = async () => {
    if (!text.trim()) {
      setError('Please enter some text to convert to speech')
      return
    }
    if (!musicFile) {
      setError('Please upload a music file')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const apiKey = await getApiKey()

      const formData = new FormData()
      formData.append('api_key', apiKey)
      formData.append('text', text)
      formData.append('language', language)
      formData.append('pause_duration', pauseDuration.toString())
      formData.append('music_volume', musicVolume.toString())
      formData.append('fade_duration', fadeDuration.toString())
      formData.append('speed', speed.toString())
      formData.append('music', musicFile)

      const response = await fetch(`${apiBaseUrl}/tts/with-music`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to generate speech with music')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while generating speech with music')
    } finally {
      setLoading(false)
    }
  }

  const transcribeAudio = async () => {
    if (!audioFile) {
      setError('Please upload an audio file to transcribe')
      return
    }

    setLoading(true)
    setError(null)
    setSttResult(null)

    try {
      const apiKey = await getApiKey()

      const formData = new FormData()
      formData.append('audio', audioFile)
      formData.append('api_key', apiKey)

      const response = await fetch(`${apiBaseUrl}/tts/stt`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to transcribe audio')
        setLoading(false)
        return
      }

      const data = await response.json()
      setSttResult(data.text || 'No transcription available')
    } catch (err: any) {
      setError(err.message || 'An error occurred while transcribing audio')
    } finally {
      setLoading(false)
    }
  }

  const changeVoiceSpeed = async () => {
    if (!audioFile) {
      setError('Please upload an audio file')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const formData = new FormData()
      formData.append('audio', audioFile)
      formData.append('speed', speed.toString())

      const response = await fetch(`${apiBaseUrl}/tts/change-voice`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to change voice speed')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while changing voice speed')
    } finally {
      setLoading(false)
    }
  }

  const addMusicToAudio = async () => {
    if (!audioFile) {
      setError('Please upload an audio file')
      return
    }
    if (!musicFile) {
      setError('Please upload a music file')
      return
    }

    setLoading(true)
    setError(null)
    setAudioUrl(null)

    try {
      const apiKey = await getApiKey()

      const formData = new FormData()
      formData.append('audio', audioFile)
      formData.append('music', musicFile)
      formData.append('api_key', apiKey)
      formData.append('music_volume', musicVolume.toString())
      formData.append('fade_duration', fadeDuration.toString())

      const response = await fetch(`${apiBaseUrl}/tts/add-music`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        setError(errorData.error || errorData.message || 'Failed to add music to audio')
        setLoading(false)
        return
      }

      const audioBlob = await response.blob()
      const url = URL.createObjectURL(audioBlob)
      setAudioUrl(url)
    } catch (err: any) {
      setError(err.message || 'An error occurred while adding music to audio')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'basic' as TabType, label: 'Basic TTS', icon: '🎙️' },
    { id: 'stream' as TabType, label: 'Streaming', icon: '🔴' },
    { id: 'batch' as TabType, label: 'Batch', icon: '📦' },
    { id: 'with-music' as TabType, label: 'With Music', icon: '🎵' },
    { id: 'stt' as TabType, label: 'Speech-to-Text', icon: '📝' },
    { id: 'change-voice' as TabType, label: 'Change Speed', icon: '⚡' },
    { id: 'add-music' as TabType, label: 'Add Music', icon: '🎶' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 transition-colors">
      <Sidebar />

      <div className="lg:pl-64">
        <DashboardHeader title="Text-to-Speech" />

        <main className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-5xl mx-auto">
            {/* Persistent Batch Processing Notification */}
            {batchProgress && batchProgress.status === 'processing' && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600 rounded-lg shadow-sm animate-fade-in">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                          Batch processing in progress...
                        </p>
                        {batchProgress.progressPercentage !== undefined && (
                          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {batchProgress.progressPercentage}%
                          </span>
                        )}
                      </div>
                      <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mt-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                          style={{ width: `${batchProgress.progressPercentage || 0}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                        Processing {batchProgress.textLength.toLocaleString()} characters. You can safely switch tabs - progress is saved automatically.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('batch')}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium ml-4"
                  >
                    View Progress →
                  </button>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="mb-8 animate-fade-in">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
                Text-to-Speech Generator 🎙️
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Convert your text into high-quality speech audio
              </p>
            </div>

            {/* Tabs */}
            <div className="mb-6 flex flex-wrap gap-2 border-b border-gray-200 dark:border-slate-700">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-t-lg font-medium transition ${
                    activeTab === tab.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Main Card */}
            <div className="glass-card rounded-xl p-6 mb-6">
              {/* Basic TTS Tab */}
              {activeTab === 'basic' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Enter Text
                    </label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type or paste the text you want to convert to speech..."
                      className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                        text.length > 0 && new Blob([text]).size > 5000
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-slate-600'
                      }`}
                      rows={6}
                    />
                    {(() => {
                      const textBytes = new Blob([text]).size
                      const isOverLimit = textBytes > 5000
                      return (
                        <div className="mt-2">
                          <p className={`text-sm ${isOverLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                            {text.length} characters • {textBytes.toLocaleString()} bytes / 5,000 bytes limit
                            {textBytes > 0 && (
                              <span className="ml-2">
                                • Credits: {Math.max(1, Math.ceil(text.length / 1000))} per 1000 characters
                              </span>
                            )}
                          </p>
                          {isOverLimit && (
                            <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                              <p className="text-sm font-medium text-red-700 dark:text-red-400">
                                ⚠️ Text exceeds 5,000 byte limit
                              </p>
                              <p className="text-sm text-red-600 dark:text-red-500 mt-1">
                                For long text, please use the <strong>Streaming</strong> or <strong>Batch</strong> tabs which automatically handle text splitting.
                              </p>
                            </div>
                          )}
                          {textBytes > 0 && textBytes <= 5000 && textBytes > 4000 && (
                            <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                              <p className="text-xs text-yellow-700 dark:text-yellow-400">
                                ⚠️ Approaching 5,000 byte limit. Consider using Streaming or Batch for longer text.
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        value={language}
                        onChange={(e) => {
                          setLanguage(e.target.value)
                          setVoiceName('')
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="en">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Voice {loadingVoices && <span className="text-xs text-gray-500">(Loading...)</span>}
                      </label>
                      {availableVoices.length > 0 ? (
                        <select
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Default (Auto-select)</option>
                          {availableVoices.map((voice) => (
                            <option key={voice.name} value={voice.name}>
                              {voice.friendly_name || voice.name} {voice.ssml_gender ? `(${voice.ssml_gender})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          placeholder="Auto-selects best voice for language"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Speed: {speed.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min="0.25"
                        max="4.0"
                        step="0.1"
                        value={speed}
                        onChange={(e) => setSpeed(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Pitch: {pitch > 0 ? '+' : ''}{pitch.toFixed(1)} semitones
                      </label>
                      <input
                        type="range"
                        min="-20"
                        max="20"
                        step="0.1"
                        value={pitch}
                        onChange={(e) => setPitch(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Volume: {volumeGainDb > 0 ? '+' : ''}{volumeGainDb.toFixed(1)} dB
                      </label>
                      <input
                        type="range"
                        min="-96"
                        max="16"
                        step="0.1"
                        value={volumeGainDb}
                        onChange={(e) => setVolumeGainDb(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <button
                    onClick={generateTTS}
                    disabled={loading || !text.trim()}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Generating Speech...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        </svg>
                        <span>Generate Speech</span>
                      </>
                    )}
                  </button>
                </>
              )}

              {/* Streaming Tab */}
              {activeTab === 'stream' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Enter Text
                    </label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type or paste the text you want to stream..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      rows={6}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="en">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Voice {loadingVoices && <span className="text-xs text-gray-500">(Loading...)</span>}
                      </label>
                      {availableVoices.length > 0 ? (
                        <select
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Default (Auto-select)</option>
                          {availableVoices.map((voice) => (
                            <option key={voice.name} value={voice.name}>
                              {voice.friendly_name || voice.name} {voice.ssml_gender ? `(${voice.ssml_gender})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          placeholder="Auto-selects best voice for language"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Chunk Size: {chunkSize} characters
                      </label>
                      <input
                        type="range"
                        min="50"
                        max="500"
                        step="10"
                        value={chunkSize}
                        onChange={(e) => setChunkSize(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Speed: {speed.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min="0.25"
                        max="4.0"
                        step="0.1"
                        value={speed}
                        onChange={(e) => setSpeed(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <button
                    onClick={generateStream}
                    disabled={loading || !text.trim()}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Streaming Speech...' : 'Start Streaming'}
                  </button>
                </>
              )}

              {/* Batch Tab */}
              {activeTab === 'batch' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Enter Text (Long text will be split into chunks)
                    </label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type or paste long text to process in batches..."
                      className={`w-full px-4 py-3 border rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none ${
                        text.length > 20000
                          ? 'border-red-500 dark:border-red-500'
                          : 'border-gray-300 dark:border-slate-600'
                      }`}
                      rows={8}
                    />
                    {text.length > 0 && (
                      <div className="mt-2">
                        <p className={`text-sm ${text.length > 20000 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                          {text.length.toLocaleString()} characters / 20,000 maximum
                          {text.length > 20000 && (
                            <span className="ml-2 font-semibold">⚠️ Text exceeds maximum length for batch processing</span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        value={language}
                        onChange={(e) => {
                          setLanguage(e.target.value)
                          setVoiceName('')
                        }}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="en">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Voice {loadingVoices && <span className="text-xs text-gray-500">(Loading...)</span>}
                      </label>
                      {availableVoices.length > 0 ? (
                        <select
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        >
                          <option value="">Default (Auto-select)</option>
                          {availableVoices.map((voice) => (
                            <option key={voice.name} value={voice.name}>
                              {voice.friendly_name || voice.name} {voice.ssml_gender ? `(${voice.ssml_gender})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={voiceName}
                          onChange={(e) => setVoiceName(e.target.value)}
                          placeholder="Auto-selects best voice for language"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Pause Duration: {pauseDuration}ms
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="2000"
                        step="100"
                        value={pauseDuration}
                        onChange={(e) => setPauseDuration(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Speed: {speed.toFixed(1)}x
                      </label>
                      <input
                        type="range"
                        min="0.25"
                        max="4.0"
                        step="0.1"
                        value={speed}
                        onChange={(e) => setSpeed(parseFloat(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Batch Progress Indicator */}
                  {batchProgress && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {batchProgress.status === 'processing' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          )}
                          {batchProgress.status === 'completed' && (
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {batchProgress.status === 'error' && (
                            <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {batchProgress.status === 'processing' && 'Processing batch... (You can switch tabs safely)'}
                            {batchProgress.status === 'completed' && 'Batch processing completed!'}
                            {batchProgress.status === 'error' && 'Batch processing failed'}
                          </span>
                          {batchProgress.status === 'processing' && batchProgress.progressPercentage !== undefined && (
                            <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 ml-2">
                              {batchProgress.progressPercentage}%
                            </span>
                          )}
                        </div>
                        <button
                          onClick={clearBatchProgress}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Clear
                        </button>
                      </div>
                      {batchProgress.status === 'processing' && (
                        <>
                          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5 mb-2 mt-2">
                            <div
                              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                              style={{ width: `${batchProgress.progressPercentage || 0}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            Processing {batchProgress.textLength.toLocaleString()} characters... This may take a while for long texts.
                          </div>
                        </>
                      )}
                      {batchProgress.status === 'completed' && (
                        <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                          Successfully generated audio from {batchProgress.textLength.toLocaleString()} characters
                        </div>
                      )}
                      {batchProgress.status === 'error' && batchProgress.error && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                          {batchProgress.error}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={generateBatch}
                    disabled={loading || !text.trim() || (batchProgress?.status === 'processing')}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Processing Batch...' : batchProgress?.status === 'processing' ? 'Processing...' : 'Generate Batch Speech'}
                  </button>
                </>
              )}

              {/* With Music Tab */}
              {activeTab === 'with-music' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Enter Text
                    </label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type or paste the text you want to convert to speech with background music..."
                      className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      rows={6}
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Music File (MP3, WAV, etc.)
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setMusicFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                      >
                        <option value="en">English (US)</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Music Volume: {musicVolume} dB
                      </label>
                      <input
                        type="range"
                        min="-40"
                        max="0"
                        step="1"
                        value={musicVolume}
                        onChange={(e) => setMusicVolume(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fade Duration: {fadeDuration}ms
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="5000"
                        step="100"
                        value={fadeDuration}
                        onChange={(e) => setFadeDuration(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <button
                    onClick={generateWithMusic}
                    disabled={loading || !text.trim() || !musicFile}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Generating with Music...' : 'Generate Speech with Music'}
                  </button>
                </>
              )}

              {/* Speech-to-Text Tab */}
              {activeTab === 'stt' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Audio File (MP3, WAV, etc.)
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <button
                    onClick={transcribeAudio}
                    disabled={loading || !audioFile}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Transcribing...' : 'Transcribe Audio'}
                  </button>

                  {sttResult && (
                    <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-2">
                        Transcription:
                      </p>
                      <p className="text-gray-900 dark:text-white">{sttResult}</p>
                    </div>
                  )}
                </>
              )}

              {/* Change Voice Speed Tab */}
              {activeTab === 'change-voice' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Audio File
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Speed: {speed.toFixed(1)}x
                    </label>
                    <input
                      type="range"
                      min="0.25"
                      max="4.0"
                      step="0.1"
                      value={speed}
                      onChange={(e) => setSpeed(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <button
                    onClick={changeVoiceSpeed}
                    disabled={loading || !audioFile}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Processing...' : 'Change Speed'}
                  </button>
                </>
              )}

              {/* Add Music Tab */}
              {activeTab === 'add-music' && (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Audio File
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white mb-4"
                    />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Music File
                    </label>
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setMusicFile(e.target.files?.[0] || null)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Music Volume: {musicVolume} dB
                      </label>
                      <input
                        type="range"
                        min="-40"
                        max="0"
                        step="1"
                        value={musicVolume}
                        onChange={(e) => setMusicVolume(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fade Duration: {fadeDuration}ms
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="5000"
                        step="100"
                        value={fadeDuration}
                        onChange={(e) => setFadeDuration(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <button
                    onClick={addMusicToAudio}
                    disabled={loading || !audioFile || !musicFile}
                    className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition"
                  >
                    {loading ? 'Adding Music...' : 'Add Music to Audio'}
                  </button>
                </>
              )}

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Audio Player */}
              {audioUrl && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <p className="text-sm font-medium text-green-700 dark:text-green-400 mb-3">
                    ✅ Audio generated successfully!
                  </p>
                  <audio controls className="w-full" src={audioUrl}>
                    Your browser does not support the audio element.
                  </audio>
                  <div className="mt-3 flex space-x-2">
                    <a
                      href={audioUrl}
                      download="output.wav"
                      className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition"
                    >
                      Download Audio
                    </a>
                    <button
                      onClick={() => {
                        setAudioUrl(null)
                        URL.revokeObjectURL(audioUrl)
                      }}
                      className="px-4 py-2 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}
